import { Box, Card, Divider, Skeleton, Stack } from '@mui/material';

type SectionSkeletonProps = {
  rows?: number;
};

function SectionSkeleton({ rows = 2 }: SectionSkeletonProps) {
  return (
    <Card
      sx={{
        p: 3,
        pt: 1.5,
        boxShadow: 'none',
        border: (theme) => `1px solid ${theme.palette.divider}`,
        backgroundColor: 'inherit',
      }}
    >
      <Stack>
        <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
          <Skeleton width={112} height={24} />
          <Skeleton variant="circular" width={32} height={32} />
        </Stack>

        <Divider sx={{ mt: 1.5 }} />

        <Stack mt={1.5} spacing={0.5}>
          {Array.from({ length: rows }, (_, index) => (
            <Stack
              key={index}
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              spacing={1}
              minHeight={20}
            >
              <Skeleton width={`${24 + ((index * 7) % 13)}%`} height={20} />
              <Skeleton width={`${18 + ((index * 11) % 17)}%`} height={20} />
            </Stack>
          ))}
        </Stack>
      </Stack>
    </Card>
  );
}

/**
 * Stands in for `VoterProfile`, both while the profile is being fetched and while the chunk
 * holding it arrives, so the two waits look the same to whoever is watching. The section shapes
 * track what the About tab renders: demographics, political, turnout, location and household.
 */
export default function VoterProfileSkeleton() {
  return (
    <Box aria-label="Loading voter profile" aria-busy="true">
      <Stack direction="row" alignItems="center" spacing={1.5}>
        <Skeleton variant="circular" width={40} height={40} sx={{ flexShrink: 0 }} />
        <Skeleton width={190} height={32} />
      </Stack>

      <Stack
        direction="row"
        alignItems="center"
        sx={{
          height: 48,
          borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
          mt: 1,
        }}
      >
        {[72, 76].map((width) => (
          <Box key={width} display="flex" justifyContent="center" flex={1}>
            <Skeleton width={width} height={20} />
          </Box>
        ))}
      </Stack>

      <Stack pt={2} px={0.5} pb={0.5} spacing={2} sx={{ minHeight: 400 }}>
        <SectionSkeleton rows={6} />
        <SectionSkeleton rows={4} />
        <SectionSkeleton rows={2} />
        <Skeleton variant="rectangular" sx={{ width: 1, height: 200, borderRadius: 1 }} />
        <SectionSkeleton rows={3} />
      </Stack>
    </Box>
  );
}
